import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// ============ TUS IMÁGENES (con continuidad visual) ============
const IMG_NATURAL = '/assets/hero/slider-natural.webp';  // Izquierda: comida natural cocinada
const IMG_KIBBLE = '/assets/hero/slider-kibble.webp';    // Derecha: pienso seco
const IMG_HERO_LOGO = '/assets/logo/realimenta-hero-logo.webp';

// ============ MAIN HERO COMPONENT ============
export const InteractiveHero = () => {
  const [sliderValue, setSliderValue] = useState(50);

  const handleSliderChange = (e) => {
    setSliderValue(Number(e.target.value));
  };

  return (
    <section 
      className="relative min-h-[92vh] flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F9F9F8 0%, #F5F3EF 100%)' }}
      data-testid="hero-section"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 left-5 w-64 h-64 bg-[#C4CEB5]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-5 w-80 h-80 bg-[#F5DC7A]/10 rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full py-10">
        
        {/* Logo imagen en la parte superior del hero - GRANDE, sin caja oscura */}
        <div className="flex justify-center mb-8 lg:mb-12">
          <img 
            src={IMG_HERO_LOGO} 
            alt="REALimenta Natural Vet" 
            className="w-auto object-contain"
            loading="eager"
            decoding="async"
            style={{ 
              height: 'clamp(100px, 18vw, 200px)',
              maxWidth: '600px'
            }}
          />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          
          {/* ===== LEFT: Copy + CTAs ===== */}
          <div className="order-2 lg:order-1">
            <p className="text-[#3D6B4F] text-lg mb-3" style={{ fontFamily: 'Caveat, cursive' }}>
              100% Natural · Sin ultraprocesados
            </p>
            
            <h1 
              className="text-[#1A2E22] tracking-tight leading-[1.08] mb-5"
              style={{ 
                fontFamily: 'Manrope, sans-serif',
                fontSize: 'clamp(1.85rem, 4.5vw, 3rem)',
                fontWeight: 800
              }}
              data-testid="hero-h1"
            >
              Nutrición natural personalizada para tu{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}>
                perro o gato
              </span>
              , con seguimiento online.
            </h1>
            
            <p className="text-[#526D5B] text-base md:text-lg mb-7 leading-relaxed max-w-lg" data-testid="hero-subtitle">
              Plan a medida basada en ciencia y adaptada a su salud y rutina. 
              Base en Málaga, atención online en toda España.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <Link
                to="/como-funciona"
                className="inline-flex items-center justify-center gap-2 bg-[#3D6B4F] text-white hover:bg-[#2C4E39] transition-all duration-200 rounded-full px-7 py-3.5 font-bold text-base group"
                style={{ boxShadow: '0 4px 20px rgba(61,107,79,0.25)' }}
                data-testid="hero-cta-primary"
              >
                Reservar consulta
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/servicios"
                className="inline-flex items-center justify-center border-2 border-[#3D6B4F] text-[#3D6B4F] hover:bg-[#3D6B4F] hover:text-white transition-all duration-200 rounded-full px-6 py-3 font-semibold text-base"
                data-testid="hero-cta-secondary"
              >
                Ver servicios
              </Link>
            </div>
          </div>
          
          {/* ===== RIGHT: Before/After Slider ===== */}
          <div className="order-1 lg:order-2" data-testid="hero-slider-card">
            
            {/* CONTENEDOR REVEAL */}
            <div 
              className="reveal relative w-full mx-auto overflow-hidden select-none"
              style={{ 
                maxWidth: '640px',
                aspectRatio: '4 / 3',
                borderRadius: '20px',
                boxShadow: '0 12px 40px rgba(61,107,79,0.15)'
              }}
              data-testid="ingredient-slider"
            >
              {/* BASE: COMIDA NATURAL COCINADA (visible a la IZQUIERDA del handle) */}
              <img 
                src={IMG_NATURAL}
                alt="Comida natural cocinada en trocitos: pollo, verduras, arroz"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 1
                }}
                loading="eager"
                draggable="false"
              />
              
              {/* Label "Ingredientes reales" - se corta con el clip de revealTop */}
              <div 
                style={{ 
                  position: 'absolute',
                  top: '16px',
                  left: '16px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#1A2E22',
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}
              >
                Comida natural
              </div>
              
              {/* REVEAL: BOWL (visible a la DERECHA del handle) */}
              {/* Se revela desde la DERECHA usando clip-path */}
              <div 
                className="revealTop"
                style={{ 
                  position: 'absolute',
                  inset: 0,
                  overflow: 'hidden',
                  zIndex: 2,
                  clipPath: `inset(0 0 0 ${sliderValue}%)`,
                  transition: 'clip-path 100ms ease-out'
                }}
              >
                <img 
                  src={IMG_KIBBLE}
                  alt="Pienso seco ultraprocesado"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  loading="eager"
                  draggable="false"
                />
                
                {/* Label "Ultraprocesados" - dentro de revealTop, se corta junto con la imagen */}
                <div 
                  style={{ 
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1A2E22',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    pointerEvents: 'none'
                  }}
                >
                  Ultraprocesados
                </div>
              </div>
              
              {/* Handle - Línea vertical + Círculo */}
              <div 
                style={{ 
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${sliderValue}%`,
                  transform: 'translateX(-50%)',
                  zIndex: 4,
                  pointerEvents: 'none',
                  transition: 'left 100ms ease-out'
                }}
              >
                {/* Línea vertical */}
                <div 
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '3px',
                    backgroundColor: 'white',
                    boxShadow: '0 0 12px rgba(0,0,0,0.4)'
                  }}
                />
                
                {/* Círculo handle */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.25)'
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D6B4F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 5l-5 7 5 7" />
                    <path d="M16 5l5 7-5 7" />
                  </svg>
                </div>
              </div>
              
              {/* Input type="range" - invisible, ocupa todo */}
              <input
                type="range"
                min="0"
                max="100"
                value={sliderValue}
                onChange={handleSliderChange}
                className="slider"
                style={{ 
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'ew-resize',
                  zIndex: 5,
                  margin: 0,
                  padding: 0
                }}
                aria-label="Revelar ingredientes y ración final"
                data-testid="slider-input"
              />
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveHero;
